import { Controller, useForm } from "react-hook-form";
import { useMutation } from "@apollo/client";
import useModal from "@/hooks/useModal";
import useAmplitude from "@/hooks/useAmplitude";
import Button from "@/components/Button";
import Radio from "@/components/Radio";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import { useParams } from "react-router-dom";
import { Event } from "../../../types";
import { PAT_SUBMIT_FOR_PAYMENT_MUTATION } from "../queries";
import { EVENT_ANALYTICS_EVENTS, getEventAnalyticsData } from "@/pages/events/_utils/amplitude";

interface Props {
  event: Event;
  onClose: () => void;
  refetch: () => void;
}

const PAYEE_OPTIONS = [
  { label: "Examiner", value: "examiner" },
  { label: "Section", value: "section" }
];

export default function SubmitForPaymentModal({ event, onClose, refetch }: Props) {
  const { Modal } = useModal();
  const { eventSlug = "" } = useParams();
  const { trackEvent } = useAmplitude();
  const [patSubmitForPaymentMutation] = useMutation(PAT_SUBMIT_FOR_PAYMENT_MUTATION);

  const defaultValues = { payee: "examiner" };
  type FieldValues = typeof defaultValues;

  const {
    control,
    formState: { isValid },
    handleSubmit
  } = useForm({ defaultValues });

  const submit = handleSubmit(async (fields: FieldValues) => {
    try {
      const response = await patSubmitForPaymentMutation({
        variables: {
          id: event.id,
          examinerFeePayee: fields.payee
        }
      });
      const { message, success } = response.data?.patSubmitForPayment;

      if (success) {
        showSnackbar(message);
        trackEvent(
          EVENT_ANALYTICS_EVENTS.submittedForPayment,
          getEventAnalyticsData({
            source: "payouts",
            eventId: event.id,
            eventSlug,
            action: "submit",
            examinerFeePayee: fields.payee
          })
        );
      } else {
        if (message) {
          showErrorSnackbar("Unable to submit payment", { subtitle: message });
        } else {
          showErrorSnackbar("Unable to submit payment", { subtitle: "Please try again later." });
        }
      }
    } catch (error) {
      showErrorSnackbar("Unable to submit payment", { subtitle: "Please try again later." });
    } finally {
      refetch();
      onClose();
    }
  });

  const action = (
    <Button disabled={!isValid} onClick={submit} testId="submit-submit-for-payment-button">
      Submit for Payment
    </Button>
  );

  return (
    <Modal
      action={action}
      className="h-fit max-h-[90vh]"
      data-testid="submitforpayment-modal"
      headline="Submit for Payment"
      onClosed={onClose}
    >
      <p>
        Are you sure you want to submit this information for payment processing? Make sure any additional travel
        expenses are recorded and uploaded in an expense report along with corresponding receipts. PDR credits will be
        awarded when applicable.
      </p>
      <p className="mt-6 text-title-medium">Submit Examiner Payment To</p>
      <Controller
        control={control}
        name="payee"
        rules={{ required: "Payee is required" }}
        render={({ field: { onChange, value } }) => (
          <>
            {PAYEE_OPTIONS.map((option) => (
              <div className="py-3 px-6" key={option.value} data-testid="radio">
                <Radio
                  name={`payeeOption-${option.value}`}
                  label={option.label}
                  checked={value === option.value}
                  onChange={() => onChange(option.value)}
                />
              </div>
            ))}
          </>
        )}
      />
    </Modal>
  );
}
